/**
 * 发票 · 关联工程名称
 *
 * 【这个弹窗解决什么】
 * 发票原先只有"关联合同"一个业务归属，财务按项目归集成本时无从下手。
 * 这里给发票挂上工程名称，工程名进字典表（同租户内唯一），
 * 于是同一张工程的发票能被检索、统计与导出。
 *
 * 【两条刻意的设计】
 *  1. **可以现场新建**：既能选已有工程，也能直接敲一个新名字——
 *     让用户"先去建工程再回来关联"是多余的两步。同名由后端唯一约束复用。
 *  2. **从合同带过来的是"建议"，不是数据**。已关联合同时，把合同名预填进输入框
 *     并**明确标注仅供参考**；人不点保存就不会写入。合同表里根本没有工程名称字段，
 *     拿合同名当工程名是猜测——本项目一贯的原则是：候选 ≠ 数据，
 *     没有人在表单上确认过的值不得写进业务字段。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import { PermissionCode, type ProjectListItem } from '@ilc/shared';
import { invoiceApi, projectApi } from '@/api/endpoints';
import { useAuthStore } from '@/stores/auth';
import { performAction } from '@/utils/performAction';

export interface InvoiceProjectModalProps {
  /** 目标发票。null 表示弹窗关闭 */
  invoice: { id: string; bizNo: string; contractId: string | null } | null;
  /** 当前已关联的工程名，用于回填 */
  currentProjectName: string | null;
  onClose: () => void;
  /** 保存成功后回调，由调用方刷新列表 */
  onSaved: () => void;
}

/** 新建选项的哨兵值——antd Select 用字符串值，不能直接塞任意文本 */
const NEW_PREFIX = '__new__:';

export function InvoiceProjectModal({
  invoice,
  currentProjectName,
  onClose,
  onSaved,
}: InvoiceProjectModalProps): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.INVOICE_UPDATE_OCR);

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    const result = await performAction({
      action: () => projectApi.list(),
      successMessage: '',
      offline: () => setProjects([]),
    });
    if (result.ok && Array.isArray(result.data)) setProjects(result.data as ProjectListItem[]);
  }, []);

  /**
   * 打开弹窗时：回填当前工程 + 取合同名作为**建议**。
   *
   * 【为什么要单独取一次合同】
   * 列表行只有 contractId，没有合同名。而这里的建议值需要的是名字。
   */
  useEffect(() => {
    if (!invoice) return;
    setValue(currentProjectName ?? undefined);
    setSearch('');
    setSuggestion(null);
    void loadProjects();

    if (currentProjectName || !invoice.contractId) return;
    void (async () => {
      const result = await performAction({
        action: async () => {
          const { contractApi } = await import('@/api/endpoints');
          const detail = await contractApi.detail(invoice.contractId!);
          // 详情接口返回的是 { contract, items, ... }，合同本体在 contract 里
          return detail.contract.name;
        },
        successMessage: '',
      });
      // 只在用户还没开始输入时给建议，避免覆盖他刚敲的内容
      if (result.ok && typeof result.data === 'string') setSuggestion(result.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  /** 输入框里的自由文本，决定要不要给一条"新建"选项 */
  const trimmed = search.trim();

  const options = useMemo(() => {
    const base = projects.map((p) => ({
      value: p.name,
      label: (
        <Space size={6}>
          <span>{p.name}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {p.invoiceCount} 张
          </Typography.Text>
        </Space>
      ),
      /* 搜索用纯文本，label 是 ReactNode 时 antd 不会自动取文本 */
      searchText: p.name,
    }));

    const exists = projects.some((p) => p.name === trimmed);
    if (trimmed !== '' && !exists) {
      base.unshift({
        value: `${NEW_PREFIX}${trimmed}`,
        label: (
          <Space size={6}>
            <Tag color="blue">新建</Tag>
            <span>{trimmed}</span>
          </Space>
        ),
        searchText: trimmed,
      });
    }
    return base;
  }, [projects, trimmed]);

  const submit = async (): Promise<void> => {
    if (!invoice) return;
    const selected = value ?? '';
    const name = selected.startsWith(NEW_PREFIX)
      ? selected.slice(NEW_PREFIX.length)
      : selected.trim();
    const payload = name === '' ? null : name;

    setSaving(true);
    const result = await performAction({
      action: () => invoiceApi.setProject(invoice.id, payload),
      successMessage: payload ? `已关联工程「${payload}」` : '已解除工程关联',
      offline: () => undefined,
      offlineMessage: '后端工程接口尚未就绪，本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      onSaved();
      onClose();
    }
  };

  return (
    <Modal
      open={invoice !== null}
      title={`工程名称 —— ${invoice?.bizNo ?? ''}`}
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={saving}
      okText="保存"
      destroyOnHidden
    >
      {suggestion ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="以下是合同名称，仅供参考"
          description={
            <Space direction="vertical" size={4}>
              <span>
                该发票已关联合同「{suggestion}」。合同本身**没有**工程名称字段，
                这里只是把合同名带出来省你一次输入——**确认后才保存**，
                不确认就什么都不会写。
              </span>
              <Typography.Link onClick={() => setValue(suggestion)}>采用这个名称</Typography.Link>
            </Space>
          }
        />
      ) : null}

      <Form layout="vertical">
        <Form.Item
          label="工程名称"
          extra="可搜索已有工程，也可直接输入新名称——同名会自动复用，不会重复建。留空表示解除关联。"
        >
          <Select
            showSearch
            allowClear
            value={value}
            placeholder="选择或输入工程名称"
            disabled={!canWrite}
            /* 输入即搜：既筛本地候选，也让"新建 XXX"跟着用户敲的字出现 */
            onSearch={setSearch}
            filterOption={(input, option) =>
              String(option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(v) => setValue(v as string | undefined)}
            options={options}
            notFoundContent={trimmed === '' ? '尚无工程，直接输入即可新建' : null}
          />
        </Form.Item>
      </Form>

      {!canWrite ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          您没有「修正识别结果」权限，只能查看工程归属。
        </Typography.Text>
      ) : null}
    </Modal>
  );
}

export default InvoiceProjectModal;
